"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { BlockchainVisualizer } from "./BlockchainVisualizer";
import TextType from "./TextType";

// Animation phases
// "transition" = brief pause after intro fades out, before final typing begins
type Phase =
  | "intro"
  | "transition"
  | "finalTyping"
  | "cursorBlink"
  | "complete";

export function WendepunktSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [showContent, setShowContent] = useState(false);
  const [phase, setPhase] = useState<Phase>("intro");
  const [isHovered, setIsHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // For custom typing of "Hier ist der Ausweg"
  const [typedPrefix, setTypedPrefix] = useState(""); // "Hier ist der "
  const [typedAusweg, setTypedAusweg] = useState(""); // "Ausweg"
  const [cursorBlinkCount, setCursorBlinkCount] = useState(0);

  const prefixText = "Hier ist der ";
  const auswegText = "Ausweg";

  // Device Detection für responsive Schriftgröße
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Gerätespezifische Schriftgröße - Tablet kleiner damit Text in eine Zeile passt
  // Tablet-Bereich erweitert: iPad Pro Querformat ist 1366px
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth <= 1400;
  const headlineFontSize = isMobile
    ? "clamp(1.5rem, 5vw, 2rem)" // Mobile: kleiner, passt für < 768px
    : isTablet
    ? "clamp(1.75rem, 4vw, 2.75rem)" // Tablet: kleiner, passt für 768-1400px
    : "clamp(2.5rem, 6vw, 5rem)"; // Desktop (>1400px): original
  const headlineWhiteSpace = isMobile ? "normal" : "nowrap" as const;

  // IntersectionObserver statt Scroll-Listener + RAF
  useEffect(() => {
    if (showContent || !sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setShowContent(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: "-30% 0px 0px 0px" } // Trigger bei ~70% im Viewport
    );

    observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, [showContent]);

  // Handle when intro typing (first two texts) completes
  const handleIntroComplete = useCallback(() => {
    // First, enter transition phase - intro fades out with its cursor
    setPhase("transition");
    // After intro has faded out (600ms), start final typing
    setTimeout(() => {
      setPhase("finalTyping");
    }, 800); // 600ms for fade + 200ms pause
  }, []);

  // Custom typing animation for "Hier ist der Ausweg"
  useEffect(() => {
    if (phase !== "finalTyping") return;

    const typingSpeed = 80;
    let currentPrefixIndex = 0;
    let currentAuswegIndex = 0;
    let timeout: NodeJS.Timeout;

    const typeNextChar = () => {
      if (currentPrefixIndex < prefixText.length) {
        // Still typing the prefix
        currentPrefixIndex++;
        setTypedPrefix(prefixText.slice(0, currentPrefixIndex));
        timeout = setTimeout(typeNextChar, typingSpeed);
      } else if (currentAuswegIndex < auswegText.length) {
        // Typing "Ausweg"
        currentAuswegIndex++;
        setTypedAusweg(auswegText.slice(0, currentAuswegIndex));
        timeout = setTimeout(typeNextChar, typingSpeed);
      } else {
        // Typing complete - start cursor blink phase
        setPhase("cursorBlink");
      }
    };

    timeout = setTimeout(typeNextChar, typingSpeed);

    return () => clearTimeout(timeout);
  }, [phase]);

  // Cursor blink 3 times then hide
  useEffect(() => {
    if (phase !== "cursorBlink") return;

    const blinkInterval = setInterval(() => {
      setCursorBlinkCount((prev) => {
        if (prev >= 5) {
          // 6 state changes = 3 full blinks (on-off-on-off-on-off)
          clearInterval(blinkInterval);
          setPhase("complete");
          return prev;
        }
        return prev + 1;
      });
    }, 500); // 500ms per half-blink = 1s per full blink

    return () => clearInterval(blinkInterval);
  }, [phase]);

  // Intro texts (will be typed and deleted)
  const introTexts = ["Geschichte wiederholt sich ...", "Es sei denn, du änderst etwas ..."];

  const isComplete = phase === "complete";
  const isTypingOrBeyond =
    phase === "finalTyping" || phase === "cursorBlink" || phase === "complete";
  const showCursor =
    phase === "finalTyping" ||
    (phase === "cursorBlink" && cursorBlinkCount % 2 === 0);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden flex items-center justify-center"
      style={{ minHeight: "100vh", backgroundColor: "#0d0d0d" }}
    >
      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-48">
        {/* Headline Container - both intro and final text share this space */}
        <div
          style={{
            position: "relative",
            marginBottom: "3rem",
            minHeight: "1.2em", // Ensures container has height
          }}
        >
          {/* Intro Typing Effect - "Wach auf..." and "Die Matrix hat dich..." - LEFT ALIGNED */}
          <div
            style={{
              opacity: showContent && phase === "intro" ? 1 : 0,
              transform: showContent ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.6s ease-out, transform 1s ease-out",
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              pointerEvents: phase !== "intro" ? "none" : "auto",
              textAlign: "left",
            }}
          >
            {showContent && (phase === "intro" || phase === "transition") && (
              <TextType
                text={introTexts}
                typingSpeed={80}
                deletingSpeed={30}
                pauseDuration={1500}
                loop={false}
                showCursor={true}
                cursorCharacter="_"
                onTypingComplete={handleIntroComplete}
                className="uppercase select-none"
                style={{
                  fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
                  fontWeight: 700,
                  fontSize: headlineFontSize,
                  letterSpacing: "-0.03em",
                  lineHeight: "110%",
                  color: "#b7ab98",
                  display: "inline-block",
                  textAlign: "left",
                }}
              />
            )}
          </div>

          {/* Final Headline with styled "Ausweg" - typed character by character */}
          {/* Uses ghost text technique: invisible full text sets width, visible text types over it */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              opacity: isTypingOrBeyond ? 1 : 0,
              visibility: isTypingOrBeyond ? "visible" : "hidden",
              transition: "opacity 0.5s ease-out",
            }}
          >
            <div style={{ position: "relative", display: "inline-block" }}>
              {/* Ghost text - invisible, sets the container width */}
              <h2
                className="uppercase select-none"
                aria-hidden="true"
                style={{
                  fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
                  fontWeight: 700,
                  fontSize: headlineFontSize,
                  letterSpacing: "-0.03em",
                  lineHeight: "110%",
                  visibility: "hidden",
                  whiteSpace: headlineWhiteSpace,
                }}
              >
                {prefixText}
                <span style={{ WebkitTextStroke: "1px transparent" }}>
                  {auswegText}
                </span>
              </h2>

              {/* Visible typed text - positioned absolutely over ghost */}
              <h2
                className="uppercase select-none"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
                  fontWeight: 700,
                  fontSize: headlineFontSize,
                  letterSpacing: "-0.03em",
                  lineHeight: "110%",
                  color: "#b7ab98",
                  whiteSpace: headlineWhiteSpace,
                  margin: 0,
                }}
              >
                {/* Typed prefix: "Hier ist der " */}
                {typedPrefix}
                {/* Styled "Ausweg" - typed with outline style from the start */}
                <span
                  className="cursor-pointer"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  style={{
                    display: "inline",
                  }}
                >
                  {/* "Aus" part */}
                  <span
                    style={{
                      color: isHovered
                        ? "#3b82f6"
                        : "transparent",
                      WebkitTextStroke: isHovered
                        ? "1px #3b82f6"
                        : "1px #b7ab98",
                      textShadow: isHovered
                        ? "0 0 60px rgba(59, 130, 246, 0.6)"
                        : "none",
                      transition:
                        "color 0.4s ease-out, -webkit-text-stroke 0.4s ease-out, text-shadow 0.4s ease-out",
                    }}
                  >
                    {typedAusweg.slice(0, 3)}
                  </span>
                  {/* "weg" part */}
                  <span
                    style={{
                      color: isHovered
                        ? "#f7931a"
                        : "transparent",
                      WebkitTextStroke: isHovered
                        ? "1px #f7931a"
                        : "1px #b7ab98",
                      textShadow: isHovered
                        ? "0 0 60px rgba(247, 147, 26, 0.4)"
                        : "none",
                      transition:
                        "color 0.4s ease-out, -webkit-text-stroke 0.4s ease-out, text-shadow 0.4s ease-out",
                    }}
                  >
                    {typedAusweg.slice(3)}
                  </span>
                </span>
                {/* Cursor - shows during typing and blinks 3 times after */}
                {showCursor && (
                  <span
                    className="typing-cursor"
                    style={{
                      marginLeft: "0.1em",
                    }}
                  >
                    _
                  </span>
                )}
              </h2>
            </div>
          </div>
        </div>

        {/* Subtext - only shows after typing is complete */}
        <p
          className="max-w-2xl mx-auto select-none text-center"
          style={{
            fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 400,
            fontSize: "clamp(1rem, 2vw, 1.375rem)",
            lineHeight: "170%",
            letterSpacing: "0.01em",
            color: "hsla(37, 18%, 66%, 0.7)",
            opacity: isComplete ? 1 : 0,
            transform: isComplete ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 1s ease-out 0.3s, transform 1s ease-out 0.3s",
          }}
        >
          Was wäre, wenn es ein Geld gäbe, das nicht gegen dich arbeitet?
          <br />
          Das niemand entwerten kann.
          <br />
          Das wirklich dir gehört.
        </p>

        {/* Blockchain Visualizer - Live mempool and confirmed blocks */}
        <div
          className="mt-16"
          style={{
            opacity: isComplete ? 1 : 0,
            transform: isComplete ? "translateY(0)" : "translateY(30px)",
            transition:
              "opacity 1.2s ease-out 0.6s, transform 1.2s ease-out 0.6s",
            overflow: "hidden",
          }}
        >
          <div style={isMobile ? { transform: "scale(0.5)", transformOrigin: "top center", height: "90px" } : undefined}>
            <BlockchainVisualizer
              showPendingBlocks={isMobile ? 3 : 4}
              showConfirmedBlocks={isMobile ? 3 : 4}
              refreshInterval={30000}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
